import { Ajax } from '@shared/Ajax';
import TileLayer from 'ol/layer/Tile';
import { unByKey } from 'ol/Observable';
import OlXYZSource from 'ol/source/XYZ';
import { MapTileProgressBar } from '@openlayers/MapTileProgressBar';
import { autorun, makeObservable, observable } from 'mobx';
import BaseLayer from 'ol/layer/Base';
import { IDrawing } from './drawings/drawing-types';
import { MapWrapper } from '@openlayers/MapWrapper';
import { TreeNode } from '@shared/ui-native/Tree';
import { createDrawing } from './drawings/drawing-factory';
import * as olExtent from 'ol/extent';

export interface IMapTileResponse {
    baseUrl: string;
    bounds: IBounds;
    minZoom: number;
    maxZoom: number;
    tileSize: number;
    valid: boolean;
    layers: string;
    cs: number;
    v: number;
    status: boolean;
    rasterImages?: any[];
}

// for every zoom level we have a bounding rect
// so we can know not to requests empty tiles when map view is outside of bounds
export interface IBounds {
    1: number[][];
    2: number[][];
    3: number[][];
    4: number[][];
    5: number[][];
    6: number[][];
    7: number[][];
    8: number[][];
    9: number[][];
    10: number[][];
    11: number[][];
    12: number[][];
    13: number[][];
    14: number[][];
    15: number[][];
    16: number[][];
    17: number[][];
    18: number[][];
    19: number[][];
    20: number[][];
    21: number[][];
    22: number[][];
    23: number[][];
}

export class BackgroundMaps {
    private worksiteMap = '';
    private alignment = '';
    private logpointFolder = '';
    private rasterizedModelIds = new Set<number>(); // models from filetree that will show as mesh
    private models: string[] = [];
    private lastRequestLayers = '';

    private activeLayers: BaseLayer[] = [];
    private activeDrawings: IDrawing[] = [];

    private activePromises: Set<string> = new Set();
    private cancelledPromises: Set<string> = new Set();

    // openlayers
    public readonly gmapsLayer = new TileLayer();
    @observable layerOpacity = 100;
    private readonly minOpacity = 0.1;

    // progress bar
    private bgMapsLayerEventKeys: (OlEventsKey | OlEventsKey[])[] = [];

    constructor(private mapWrapper: MapWrapper, public progressBar: MapTileProgressBar) {
        makeObservable(this);
        autorun(() => {
            this.setOpacity(this.layerOpacity);
        });
    }

    getJoinedString(): string {
        const all = [...this.models, this.worksiteMap, this.alignment, this.logpointFolder];

        this.rasterizedModelIds.forEach(id => {
            all.push('m_' + id);
        });

        return all.join(',');
    }

    add(id: number | string) {
        if ($.isNumeric(id)) {
            this.addModel(id as number);
        } else {
            if (!this.models.includes(id as string)) {
                this.models.push(id as string);
            }
        }
    }

    addTypedMap(type: 'm' | 'o' | 'r', id: number) {
        if (!$.isNumeric(id)) return;

        const model = type + '_' + id;

        if (!this.models.includes(model)) {
            this.models.push(model);
        }
    }

    async createRasterLayerForModel(uuid: string, node?: TreeNode): Promise<BaseLayer> {
        if (this.activeDrawings.find(drawing => drawing.uuid === uuid)) {
            return;
        }

        const drawing: IDrawing | null = await createDrawing(uuid, node);

        if (!drawing) {
            return;
        }

        drawing.layerGroup.setOpacity(utils.clamp(this.minOpacity, this.layerOpacity / 100, 1));
        this.activeLayers.push(drawing.layerGroup);
        this.activeDrawings.push(drawing);
        return drawing.layerGroup;
    }

    removeDrawing(drawingUuid: string): void {
        const drawingToBeRemoved = this.activeDrawings.find(drawing => drawing.uuid === drawingUuid);

        if (!drawingToBeRemoved) return;

        drawingToBeRemoved.dispose();
        this.activeDrawings = this.activeDrawings.filter(drawing => drawing.uuid !== drawingUuid);
    }

    addModel(modelId: number) {
        this.addTypedMap('m', modelId);
    }

    getRasterizedModelIds(): Set<number> {
        return this.rasterizedModelIds;
    }

    toggleRasterizedModelId(modelId: number) {
        if (this.rasterizedModelIds.has(modelId)) {
            this.rasterizedModelIds.delete(modelId);
        } else {
            this.rasterizedModelIds.add(modelId);
        }
        this.loadGmaps();
    }

    createSource(mapTileResponse: IMapTileResponse): OlXYZSource {
        const { baseUrl, layers, minZoom, maxZoom, tileSize, v, cs, bounds } = mapTileResponse;

        const source = new OlXYZSource({
            tileUrlFunction: (coord: any) => {
                const [zoom, x, y] = coord;

                // if position is out of bounds, don't request the tile
                if (zoom < minZoom || zoom > maxZoom || !bounds[zoom] || bounds[zoom][0][0] > x || x > bounds[zoom][1][0] || bounds[zoom][0][1] > y || y > bounds[zoom][1][1]) {
                    return undefined;
                }

                return `${kuura.getContextPath()}${baseUrl}?layers=${layers}&cs=${cs}&v=${v}&x=${x}&y=${y}&z=${zoom}`;
            },
            minZoom,
            maxZoom,
            tileSize,
        });

        return source;
    }

    getExtentForDrawing(uuid: string) {
        const drawing = this.activeDrawings.find(drawing => drawing.uuid === uuid);

        if (!drawing) return null;

        return drawing.extent;
    }

    getLoadedDrawingExtents(): olExtent.Extent {
        const extent = olExtent.createEmpty();
        this.activeDrawings.forEach(drawing => olExtent.extend(extent, drawing.extent));
        console.log(extent);
        return extent;
    }

    loadGmaps = _.debounce((): JQueryPromise<any> => {
        const mapIdsJoined = this.getJoinedString();
        if (this.lastRequestLayers == mapIdsJoined) return;

        return Ajax({
            url: `gmaps_tile_bounds.json`,
            data: {
                layers: mapIdsJoined,
                projectId: kuura.getProjectId(),
            },
        }).done((resp: IMapTileResponse) => {
            if (resp.layers) {
                const source = this.createSource(resp);

                if (this.progressBar) {
                    this.bgMapsLayerEventKeys.forEach(unByKey);
                    this.progressBar.reset();
                    this.bgMapsLayerEventKeys = this.progressBar.attachEvents(source);
                }
                this.gmapsLayer.setSource(source);
                this.lastRequestLayers = mapIdsJoined;
                setTimeout(() => {
                    // sometimes setSource will not refresh so we have to force it
                    this.gmapsLayer.getSource().refresh();
                }, 100);
            }
        });
    }, 100);

    loadWorksite() {
        const defaultMap = kuura.getProjectWorksiteMapModel();
        if (defaultMap?.id) {
            this.onModelChecked(defaultMap.uuid);
        }
    }

    setOpacity = (opacity: number) => {
        if (typeof opacity === 'number') {
            const safe = utils.clamp(this.minOpacity, opacity / 100, 1);
            this.gmapsLayer.setOpacity(safe);
            this.activeLayers.forEach(layer => layer.setOpacity(safe));
        } else {
            this.gmapsLayer.setOpacity(1);
            this.activeLayers.forEach(layer => layer.setOpacity(1));
        }
    };

    onModelChecked = async (uuid: string, node?: TreeNode) => {
        this.activePromises.add(uuid);

        let layer: BaseLayer | null = null;
        try {
            layer = await this.createRasterLayerForModel(uuid, node);
        } catch (e) {
            console.warn('Loading of drawing was cancelled', e);
            return null;
        }
        this.activePromises.delete(uuid);

        /* Check if the loading was cancelled when
           fetching the geojson and creating the layer.
           If it was do not load the drawing
        */
        if (this.cancelledPromises.has(uuid)) {
            this.cancelledPromises.delete(uuid);
            throw new Error('Cancelled');
        }

        if (!layer) {
            return;
        }

        this.mapWrapper.addLayer(layer, 1, uuid);
    };

    onModelUnchecked = async (uuid: string) => {
        if (this.activePromises.has(uuid)) {
            this.cancelledPromises.add(uuid);
        }

        this.mapWrapper.removeLayerById(uuid);
        this.mapWrapper.backgroundMaps.removeDrawing(uuid);
    };
}
